import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ContestTable, ContestFilter, Pagination, ContestFormModal } from '../components';
import { fetchContests, createContest, updateContest } from '../services/api';

const ContestManagement = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    heading: '',
    companyName: '',
    category: '',
    subcategory: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const fetchContestsData = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        size: 10,
        sort: 'id,desc'
      };
      
      const data = await fetchContests(params);
      setContests(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching contests:', error);
      toast.error('Failed to fetch contests. Please try again.');
      setContests([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContestsData();
  }, [filters]);

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      fetchContestsData(newPage);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleCreateContest = async (contestData) => {
    setLoading(true);
    try {
      await createContest(contestData);
      toast.success('Contest created successfully!');
      setShowCreateModal(false);
      fetchContestsData(0); // Refresh and go to first page
    } catch (error) {
      console.error('Error creating contest:', error);
      toast.error('Failed to create contest. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContest = async (contestId, contestData) => {
    setLoading(true);
    try {
      await updateContest(contestId, contestData);
      toast.success('Contest updated successfully!');
      fetchContestsData(); // Refresh current page
    } catch (error) {
      console.error('Error updating contest:', error);
      
      // Check for the specific 400 error message about active contest
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data.message;
        if (errorMessage === "One contest is already active at the moment for this category and subcategory") {
          toast.error(errorMessage);
        } else {
          toast.error('Failed to update contest. Please try again.');
        }
      } else {
        toast.error('Failed to update contest. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Contest Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Create Contest
        </button>
      </div>

      <ContestFilter onFilterChange={handleFilterChange} filters={filters} />

      <ContestTable 
        contests={contests} 
        loading={loading}
        onUpdate={handleUpdateContest}
        fetchContests={fetchContestsData}
      />

      {!loading && contests.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      
      <div className="mt-6 text-center text-sm text-gray-500">
        {!loading && contests.length > 0 && (
          <p>Showing {contests.length} of {totalElements} contests</p>
        )}
      </div>

      {showCreateModal && (
        <ContestFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateContest}
        />
      )}
    </div>
  );
};

export default ContestManagement; 